export const joinUrl = 'https://mctest.cspnow.org'
export const contactEmail = 'rmight@imcva.com'
export const supportEmail = 'support@cspnow.org'

export const hero = {
  kicker: 'Common Sense Politics',
  title: 'The political center already exists. It does not have a usable voice.',
  text: 'CSPNow is the platform Common Sense Politics built so moderates can compare notes, take polls, and put those views in front of organizations and elected officials, continuously rather than once a year.',
}

export const problem = {
  kicker: 'The problem',
  title: 'Extremes set the agenda because the center is quiet.',
  lead: 'The far left and far right dominate the news and have undue influence on elected representatives. In many states, and in the nation as a whole, partisan politics has made governance ineffectual. Compromise has become a form of political suicide.',
  points: [
    {
      title: 'Passion is not the same as a majority',
      text: 'Most political moderates are not passionate enough about any one issue to live in the daily fight. That silence lets the two extremes set legislative agendas and shape elections.',
    },
    {
      title: 'Mixed rooms become trench warfare',
      text: 'When people with radically different views try to debate a charged issue, the conversation usually hardens each side. Compromise becomes impossible, and candidates who are not already on an extreme have to pick one.',
    },
    {
      title: 'Nearby views can still produce agreement',
      text: 'Groups whose views are similar but not identical can have constructive discussions. Those talks can produce compromise that a large majority of the group can accept.',
    },
  ],
}

export const purpose = {
  kicker: 'About Common Sense Politics',
  title: 'A platform for people who are not of the far left or far right.',
  paragraphs: [
    'Common Sense Politics is a nonprofit. Its purpose is to give Americans who do not associate with either political extreme an internet-based way to communicate, coordinate, and be counted.',
    'By gathering and sharing the views of the centrist population, we aim to influence elected representatives and make bipartisanship achievable again. We believe many Americans would welcome a less confrontational approach in government, and would join and participate if they can see that it is actually making a difference.',
  ],
  mustDo: [
    'Give groups with somewhat different, but not radical, views tools to share opinions continuously and talk with people who will compromise.',
    'Prevent the kind of confrontation that turns disagreement into more partisanship.',
    'Get moderates to speak, stay engaged, and see that their participation matters.',
  ],
}

export const hurdles = [
  {
    title: 'Get moderates excited',
    text: 'Centrists will not show up for another abstract call to civility. They need a clear goal and a reason to believe their time is not wasted.',
  },
  {
    title: 'Get them into the dialogue',
    text: 'Excitement is not enough. Members need an easy way to discuss issues, vote on polls, and stay in the conversation at the national, state, and local levels.',
  },
  {
    title: 'Show it is making a difference',
    text: 'Views have to land in a format other moderate organizations and elected representatives can understand and use. That has to keep happening, not arrive once as a survey and stop.',
  },
]

export const features = [
  {
    id: 'feed',
    title: 'My Feed',
    text: 'A single place for the latest updates from every Circle you belong to.',
  },
  {
    id: 'discussions',
    title: 'Discussions',
    text: 'Start or join a conversation on a topic in your Circles, request a new topic, save favorites, and hide the ones you do not care about.',
  },
  {
    id: 'polls',
    title: 'Polls',
    text: 'See the latest polls from your Circles, vote, review Circle-by-Circle results, and create a new poll when a topic is still live.',
  },
  {
    id: 'documents',
    title: 'Documents',
    text: 'Browse folders you have access to, filter by Circle or keyword, upload files for admin review, and link out to SharePoint or Drive.',
  },
  {
    id: 'circles',
    title: 'Circles',
    text: 'See the Circles you belong to or have applied to, leave one, search nearby Circles by zip code, and request a new Circle from an administrator.',
  },
  {
    id: 'support',
    title: 'How-to and support',
    text: 'Short videos on the key features, answers to common questions, and a way to request help.',
  },
]

export const circles = {
  kicker: 'How membership works',
  title: 'Circles follow the levels of government people already live in.',
  lead: 'CSPNow is organized so members can talk at the same levels where decisions are actually made. The full organizational structure is better walked through in a conversation than in a diagram dumped into an email.',
  levels: [
    {
      title: 'National',
      text: 'Every member belongs here.',
    },
    {
      title: 'State',
      text: 'You are enrolled in the state that matches the zip code you give when you join.',
    },
    {
      title: 'Metro',
      text: 'Optional Circles for issues that cross city and county lines inside a state.',
    },
    {
      title: 'Partner',
      text: 'Each partner organization gets its own Circle, then can request membership in local Circles or ask that new ones be created.',
    },
  ],
}

export const partnerBenefits = [
  {
    title: 'Reach people you do not already have',
    text: 'Attract new members from the Common Sense Politics user base, and give your existing members a new way to stay engaged.',
  },
  {
    title: 'A Circle of your own, without building a platform',
    text: 'Every partner gets a Circle they can use to communicate, coordinate, and publish surveys to their own members, without standing up and maintaining a separate discussion tool.',
  },
  {
    title: 'Topics and polls without a research budget',
    text: 'Create and share discussion topics and polls of interest to your organization. CSPNow can return results on a hot topic while traditional surveys are still in the field.',
  },
  {
    title: 'A view of the wider center',
    text: 'See opinions from the full Common Sense Politics user base, not only the people already on your list.',
  },
  {
    title: 'Less spent on finding the next member',
    text: 'Members of Common Sense Politics also belong to like-minded moderate organizations and can reach your website from CSPNow.',
  },
  {
    title: 'Discussion at every level of government',
    text: 'Your members can participate in political discussion nationally, in their state, and locally, without you having to build that infrastructure.',
  },
]

export const meetingNote = {
  title: 'What we walk through in a meeting',
  text: 'The organizational structure, how your Circle would sit inside it, and how your organization can best use CSPNow. That is the conversation, not a PDF attachment.',
}

export const walkthrough = {
  kicker: 'The next step',
  title: 'Start with a 30-minute walkthrough.',
  text: 'We show you the platform live and walk through how your Circle would sit inside the structure. You bring the questions your members argue about. Nothing is set up until you decide to go ahead.',
  action: 'Request a walkthrough',
}

export const integrationSteps = [
  {
    title: 'We walk through it together',
    text: 'Thirty minutes, live. You see the platform and the organizational structure, and we answer the questions a document cannot.',
  },
  {
    title: 'Your Circle is created',
    text: 'Your organization gets its own Circle and you are its administrator. You review who joins, and nothing goes out under your name that you did not send.',
  },
  {
    title: 'Your first topic and poll go up',
    text: 'You bring one discussion topic and one poll question. Your members get short how-to videos, an FAQ, and a way to request help.',
  },
  {
    title: 'You see where people landed',
    text: 'You get your own members’ answers, and the answers from the wider Common Sense Politics membership to the same question, back faster than a traditional survey can turn around.',
  },
]

export const useCases = {
  kicker: 'What you would put in it',
  title: 'Every membership organization has these four problems.',
  lead: 'A Circle is not another newsletter. It is the place where the questions you already have get answered by the people you already represent.',
  items: [
    {
      title: 'A question you would otherwise guess at',
      text: 'You are about to take a public position and you do not actually know where your members are on it. Put it up as a poll and find out this week instead of assuming.',
    },
    {
      title: 'A conversation that outlives the event',
      text: 'The workshop, panel, or webinar goes well and then it ends. A discussion topic keeps it going afterward, with the people who were in the room and the ones who could not make it.',
    },
    {
      title: 'The members you never hear from',
      text: 'The same handful of people reply to everything. A poll gets an answer out of the quiet majority, so you can tell the difference between agreement and disengagement.',
    },
    {
      title: 'Documents nobody can find',
      text: 'Position papers, briefs, and toolkits scattered across email threads and drives. Your Circle gives them one place, searchable, with links out to what you already keep in SharePoint or Drive.',
    },
  ],
}
